/**
 * Daily Reset System
 * 
 * Client-side utilities for interacting with the server-side daily reset system.
 * Uses Firebase Cloud Functions to ensure secure, cheat-proof reset logic.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { ClientStateSnapshot, EnsurePlayerReadyResponse } from './stateSync';

export interface DailyResetResult {
  resetApplied: boolean;
  player: any;
  message?: string;
  staminaRestored?: number;
  hpRestored?: number;
  hoursUntilReset?: string;
  // Sync fields
  syncRequired?: boolean;
  syncIssues?: Array<{
    field: string;
    client: any;
    server: any;
  }>;
  gameplayState?: any;
}

/**
 * Check if player needs daily reset and apply it automatically
 * Should be called before any action that requires stamina
 * 
 * Sends player's timezone offset to server for midnight calculation
 * (e.g., -300 for EST, meaning UTC-5)
 * 
 * NEW: Also sends client state snapshot for sync validation
 * If server detects mismatch, returns syncRequired: true
 * 
 * @param clientState - Optional client state snapshot for sync validation
 * @returns Result object with updated player data and sync status
 */
export async function ensurePlayerReady(
  clientState?: ClientStateSnapshot
): Promise<DailyResetResult> {
  try {
    const functions = getFunctions(getApp());
    const ensurePlayerReadyFn = httpsCallable<
      { timezoneOffset: number; clientState?: ClientStateSnapshot },
      EnsurePlayerReadyResponse
    >(
      functions,
      'ensurePlayerReady'
    );

    // Get player's timezone offset in minutes
    // Negative for timezones ahead of UTC (e.g., -300 for EST = UTC-5)
    const timezoneOffset = new Date().getTimezoneOffset();

    const result = await ensurePlayerReadyFn({ 
      timezoneOffset,
      clientState,
    });
    
    return result.data;
  } catch (error) {
    console.error('Error calling ensurePlayerReady:', error);
    throw error;
  }
}

/**
 * Format hours until reset as a readable string
 * 
 * @param hours - Hours until reset (as decimal, e.g., 12.5)
 * @returns Formatted string (e.g., "12 hours 30 minutes")
 */
export function formatTimeUntilReset(hours: number): string {
  const wholeHours = Math.floor(hours);
  const minutes = Math.round((hours - wholeHours) * 60);
  
  if (wholeHours === 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  
  if (minutes === 0) {
    return `${wholeHours} hour${wholeHours !== 1 ? 's' : ''}`;
  }
  
  return `${wholeHours} hour${wholeHours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
}

/**
 * Get out-of-stamina message for the user
 * 
 * @param hoursUntilReset - Optional hours until reset (no longer displayed)
 * @returns User-friendly message
 */
export function getOutOfStaminaMessage(hoursUntilReset?: string): string {
  // Simple message - player doesn't need to see countdown
  return 'You are out of stamina and must rest. Your stamina and health will recover tomorrow.';
}

